import { useState, useEffect, useRef, useMemo } from "react";
import "./App.css";
import SearchForm from "./components/SearchForm";
import ResultsDisplay from "./components/ResultsDisplay";
import InfoTooltip from "./components/InfoTooltip";
import { runScanWithPolling, cancelActiveScan } from "./api";
import { Platform, ScanRequest, ScanResponse } from "./types";

const BASE_TITLE = "Stockfished";

interface InitialFormValues {
  username: string;
  platform: Platform;
  monthsBack: number;
}

/** Parse ?u=...&p=...&m=... from the current URL. Invalid values → ignored. */
function readUrlParams(): InitialFormValues | null {
  if (typeof window === "undefined") return null;
  const sp = new URLSearchParams(window.location.search);
  const u = (sp.get("u") || "").trim();
  const p = sp.get("p");
  const mRaw = sp.get("m");
  if (!u || !p || !mRaw) return null;
  if (p !== "chesscom" && p !== "lichess" && p !== "both") return null;
  const m = parseInt(mRaw, 10);
  if (!Number.isInteger(m) || m < 1 || m > 12) return null;
  return { username: u, platform: p, monthsBack: m };
}

/** Write ?u=...&p=...&m=... to the address bar without adding a history entry. */
function writeUrlParams(req: ScanRequest): void {
  if (typeof window === "undefined") return;
  const sp = new URLSearchParams();
  sp.set("u", req.username);
  sp.set("p", req.platform);
  sp.set("m", String(req.months_back));
  const newUrl = `${window.location.pathname}?${sp.toString()}`;
  window.history.replaceState({}, "", newUrl);
}

function App() {
  const [results, setResults] = useState<ScanResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Hydrate form from URL params on first render — shareable links pre-fill
  // the form for the recipient. Memoized so React-StrictMode's double-effect
  // doesn't re-read after the user has started editing.
  const initialValues = useMemo(() => readUrlParams(), []);

  useEffect(() => {
    if (loading) {
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [loading]);

  useEffect(() => {
    return () => cancelActiveScan();
  }, []);

  // Pageview ping. Fires once on first mount. Body carries first_today
  // = true only the first time this browser loads the site each day,
  // gated by a localStorage entry. No PII collected; uniqueness is
  // per-browser-per-day, not per-user.
  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    let firstToday = false;
    try {
      const last = localStorage.getItem("stockfished_visited");
      if (last !== today) {
        firstToday = true;
        localStorage.setItem("stockfished_visited", today);
      }
    } catch {
      // Private-mode Safari, locked-down browsers — just send the ping
      // without the first-of-day signal.
    }
    fetch("/api/visit", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ first_today: firstToday }),
      keepalive: true,
    }).catch(() => {
      // Network failures shouldn't surface to the user; this is fire-and-forget.
    });
  }, []);

  // Mirror scan state into document.title so users can switch tabs and
  // notice when the scan completes.
  useEffect(() => {
    if (loading) {
      document.title = `Scanning… (${elapsed}s) | ${BASE_TITLE}`;
    } else if (error) {
      document.title = `Scan failed | ${BASE_TITLE}`;
    } else if (results) {
      const n = results.total_unique_cheaters;
      const noun = n === 1 ? "cheater" : "cheaters";
      document.title = n === 0
        ? `No cheaters found | ${BASE_TITLE}`
        : `${n} ${noun} found | ${BASE_TITLE}`;
    } else {
      document.title = BASE_TITLE;
    }
  }, [loading, elapsed, results, error]);

  const handleSubmit = async (req: ScanRequest) => {
    setLoading(true);
    setError(null);
    setResults(null);
    // Reflect the just-submitted scan in the URL so the user can copy from
    // the address bar to share. Done before await so even if the scan errors,
    // the URL still represents the attempt.
    writeUrlParams(req);

    try {
      const data = await runScanWithPolling(req);
      setResults(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="App">
      <div className="header">
        <div className="header-title">
          <img src="/stockfish.png" alt="Stockfished" className="logo" />
          <h1>Stockfished</h1>
        </div>
        <p>
          Find out if you've played against a convicted cheater in your online chess games.
          {" "}
          <InfoTooltip label="How this works">
            We don't run our own cheat detection. We just surface what chess.com and Lichess
            have already publicly determined — accounts marked{" "}
            <code>closed:fair_play_violations</code> on chess.com, or <code>tosViolation</code>{" "}
            on Lichess.
            <br />
            <br />
            <a
              href="https://www.chess.com/legal/fair-play"
              target="_blank"
              rel="noopener noreferrer"
            >
              chess.com fair-play policy →
            </a>
            <br />
            <a
              href="https://lichess.org/page/fair-play"
              target="_blank"
              rel="noopener noreferrer"
            >
              Lichess fair-play policy →
            </a>
          </InfoTooltip>
        </p>
      </div>
      <SearchForm
        onSubmit={handleSubmit}
        loading={loading}
        initialValues={initialValues || undefined}
      />
      {loading && <div className="scanning">Scanning… {elapsed}s</div>}
      <ResultsDisplay results={results} error={error} />
    </div>
  );
}

export default App;
